/* eslint-disable max-lines */
import { openMessagesModal, openSubsamplesModal, setURLParams } from 'actions';
import { openExploreMessagesModal, openExploreSubsamplesModal } from 'actions/envelopeListView';
import { TAG_REMOVE_ICON } from 'constants/filterIcons';
import { andFilters, filterKeysMapping } from 'constants/filtersMappingValues';
import pluralize from 'pluralize';
import React from 'react';
import { useDispatch } from 'react-redux';
import { EnvelopeFilterKeys } from 'types/filters';
import { Condition, DataNode, lookAllForField, Tree, ValueNode } from 'utils/parserTree';
import { captureMessage } from 'utils/reports';
import { v4 } from 'uuid';
import AndOrBadge from './AndOrBadge';
import FilterBadge from './FilterBadge';

export type AllowDeleteField = {
  field: string;
  value: string;
};

type ComponentProps = {
  tree: Tree | DataNode | ValueNode;
  scrolled?: boolean;
  showAllFilters?: boolean;
  filtersModified?: boolean;
  selectedFilters: ValueNode[];
  selectedFields: DataNode[];
  // eslint-disable-next-line react/no-unused-prop-types
  canChangeConditional?: boolean;
  allowDelete?: boolean;
  allowDeleteFields?: AllowDeleteField[];
  field?: string;
  // eslint-disable-next-line react/no-unused-prop-types
  fieldId?: string;
  envelopesPage?: boolean;
  renderSampleButton?: boolean;
  removeAllFilter: () => void;
  removeFilter: (key: string, id: string) => void;

  handleRemoveValueFromTree: (value: string, field: string, fieldId?: string) => void;
  handleRemoveFieldFromTree: (field: string, id?: string) => void;
  handleChangeConditionalType: (node: DataNode, condition: Condition) => void;

  handleAddSelectedFilter: (node: ValueNode) => void;
  handleRemoveSelectedFilter: (node: ValueNode) => void;
  handleAddSelectedField: (node: DataNode) => void;
  handleRemoveSelectedField: (node: DataNode) => void;
  handleClearSelectedFields: () => void;
  handleClearSelectedFilters: () => void;

  isReviewSet?: boolean;
  reviewSetParentFilter?: string;
};

const FilterPillsTree: React.FC<ComponentProps> = (props) => {
  const {
    tree,
    allowDelete = true,
    allowDeleteFields,
    scrolled,
    filtersModified,
    showAllFilters,
    removeAllFilter,
    removeFilter,
    field,
    fieldId,
    envelopesPage,
    canChangeConditional,
    renderSampleButton,
    selectedFilters,
    selectedFields,
    isReviewSet = false,
    reviewSetParentFilter = '',

    handleRemoveValueFromTree,
    handleRemoveFieldFromTree,
    handleChangeConditionalType,

    handleAddSelectedFilter,
    handleRemoveSelectedFilter,
    handleAddSelectedField,
    handleRemoveSelectedField,
    handleClearSelectedFields,
    handleClearSelectedFilters,
  } = props;

  if ('value' in tree) {
    const canDeleteReviewSet =
      !isReviewSet ||
      !reviewSetParentFilter ||
      (isReviewSet && !reviewSetParentFilter.includes(tree.value));
    const useAlternateColor =
      isReviewSet && !!reviewSetParentFilter && !reviewSetParentFilter.includes(tree.value);

    const canDelete =
      allowDelete ||
      (!!field &&
        !!allowDeleteFields?.some((entry) => entry.field === field && entry.value === tree.value));

    return (
      <FilterBadge
        key={tree.id}
        title={tree.label ?? tree.value}
        node={tree as ValueNode}
        disabled={!envelopesPage}
        deleteAction={
          canDelete && canDeleteReviewSet && field
            ? (): void => {
                handleRemoveValueFromTree(tree.value, field, fieldId);
                removeFilter(field, tree.value);
              }
            : undefined
        }
        handleAddSelectedFilter={handleAddSelectedFilter}
        handleRemoveSelectedFilter={handleRemoveSelectedFilter}
        selectedFilters={selectedFilters}
        useAlternateColor={useAlternateColor}
      />
    );
  }
  if (tree.op === 'and' || tree.op === 'or') {
    return (
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      <ConditionField
        tree={tree as DataNode}
        scrolled={scrolled}
        showAllFilters={showAllFilters}
        filtersModified={filtersModified}
        removeAllFilter={removeAllFilter}
        removeFilter={removeFilter}
        field={field}
        fieldId={fieldId}
        envelopesPage={envelopesPage}
        allowDelete={allowDelete}
        allowDeleteFields={allowDeleteFields}
        key={tree.id}
        selectedFilters={selectedFilters}
        selectedFields={selectedFields}
        canChangeConditional={canChangeConditional}
        renderSampleButton={renderSampleButton}
        handleRemoveValueFromTree={handleRemoveValueFromTree}
        handleRemoveFieldFromTree={handleRemoveFieldFromTree}
        handleChangeConditionalType={handleChangeConditionalType}
        handleAddSelectedFilter={handleAddSelectedFilter}
        handleRemoveSelectedFilter={handleRemoveSelectedFilter}
        handleAddSelectedField={handleAddSelectedField}
        handleRemoveSelectedField={handleRemoveSelectedField}
        handleClearSelectedFields={handleClearSelectedFields}
        handleClearSelectedFilters={handleClearSelectedFilters}
        isReviewSet={isReviewSet}
        reviewSetParentFilter={reviewSetParentFilter}
      />
    );
  }
  if (tree.op === 'field' && 'field' in tree) {
    return (
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      <Field
        tree={tree as DataNode}
        scrolled={scrolled}
        showAllFilters={showAllFilters}
        filtersModified={filtersModified}
        removeAllFilter={removeAllFilter}
        removeFilter={removeFilter}
        field={field}
        fieldId={fieldId}
        envelopesPage={envelopesPage}
        allowDelete={allowDelete}
        allowDeleteFields={allowDeleteFields}
        key={tree.id}
        selectedFilters={selectedFilters}
        selectedFields={selectedFields}
        canChangeConditional={canChangeConditional}
        renderSampleButton={renderSampleButton}
        handleRemoveValueFromTree={handleRemoveValueFromTree}
        handleRemoveFieldFromTree={handleRemoveFieldFromTree}
        handleChangeConditionalType={handleChangeConditionalType}
        handleAddSelectedFilter={handleAddSelectedFilter}
        handleRemoveSelectedFilter={handleRemoveSelectedFilter}
        handleAddSelectedField={handleAddSelectedField}
        handleRemoveSelectedField={handleRemoveSelectedField}
        handleClearSelectedFields={handleClearSelectedFields}
        handleClearSelectedFilters={handleClearSelectedFilters}
        isReviewSet={isReviewSet}
        reviewSetParentFilter={reviewSetParentFilter}
      />
    );
  }

  if (tree.op === 'group') {
    return (
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      <Group
        tree={tree as DataNode}
        scrolled={scrolled}
        showAllFilters={showAllFilters}
        filtersModified={filtersModified}
        removeAllFilter={removeAllFilter}
        removeFilter={removeFilter}
        field={field}
        fieldId={fieldId}
        envelopesPage={envelopesPage}
        allowDelete={allowDelete}
        allowDeleteFields={allowDeleteFields}
        key={tree.id}
        selectedFilters={selectedFilters}
        selectedFields={selectedFields}
        canChangeConditional={canChangeConditional}
        renderSampleButton={renderSampleButton}
        handleRemoveValueFromTree={handleRemoveValueFromTree}
        handleRemoveFieldFromTree={handleRemoveFieldFromTree}
        handleChangeConditionalType={handleChangeConditionalType}
        handleAddSelectedFilter={handleAddSelectedFilter}
        handleRemoveSelectedFilter={handleRemoveSelectedFilter}
        handleAddSelectedField={handleAddSelectedField}
        handleRemoveSelectedField={handleRemoveSelectedField}
        handleClearSelectedFields={handleClearSelectedFields}
        handleClearSelectedFilters={handleClearSelectedFilters}
        isReviewSet={isReviewSet}
        reviewSetParentFilter={reviewSetParentFilter}
      />
    );
  }

  return <></>;
};

function Group({
  tree,
  removeAllFilter,
  showAllFilters,
  filtersModified,
  scrolled,
  removeFilter,
  field,
  fieldId,
  envelopesPage,
  allowDelete,
  allowDeleteFields,
  canChangeConditional,
  renderSampleButton,
  selectedFilters,
  selectedFields,
  isReviewSet = false,
  reviewSetParentFilter = '',
  handleRemoveValueFromTree,
  handleRemoveFieldFromTree,
  handleChangeConditionalType,
  handleAddSelectedFilter,
  handleRemoveSelectedFilter,
  handleAddSelectedField,
  handleRemoveSelectedField,
  handleClearSelectedFields,
  handleClearSelectedFilters,
}: ComponentProps): JSX.Element {
  return (
    <div className="flex flex-row items-center gap-1" key={tree.id}>
      <span>(</span>
      {(tree as DataNode).data.map((f) => (
        <FilterPillsTree
          tree={f}
          key={f.id}
          removeAllFilter={removeAllFilter}
          showAllFilters={showAllFilters}
          filtersModified={filtersModified}
          scrolled={scrolled}
          removeFilter={removeFilter}
          envelopesPage={envelopesPage}
          field={field}
          fieldId={fieldId}
          selectedFilters={selectedFilters}
          selectedFields={selectedFields}
          allowDelete={allowDelete}
          allowDeleteFields={allowDeleteFields}
          canChangeConditional={canChangeConditional}
          renderSampleButton={renderSampleButton}
          handleRemoveValueFromTree={handleRemoveValueFromTree}
          handleRemoveFieldFromTree={handleRemoveFieldFromTree}
          handleChangeConditionalType={handleChangeConditionalType}
          handleAddSelectedFilter={handleAddSelectedFilter}
          handleRemoveSelectedFilter={handleRemoveSelectedFilter}
          handleAddSelectedField={handleAddSelectedField}
          handleRemoveSelectedField={handleRemoveSelectedField}
          handleClearSelectedFields={handleClearSelectedFields}
          handleClearSelectedFilters={handleClearSelectedFilters}
          isReviewSet={isReviewSet}
          reviewSetParentFilter={reviewSetParentFilter}
        />
      ))}
      <span>)</span>
    </div>
  );
}

function Field({
  tree,
  scrolled,
  showAllFilters,
  filtersModified,
  removeAllFilter,
  envelopesPage,
  removeFilter,
  allowDelete,
  allowDeleteFields,
  renderSampleButton,
  selectedFilters,
  selectedFields,
  isReviewSet = false,
  reviewSetParentFilter = '',
  handleRemoveValueFromTree,
  handleRemoveFieldFromTree,
  handleChangeConditionalType,
  handleAddSelectedFilter,
  handleRemoveSelectedFilter,
  handleAddSelectedField,
  handleRemoveSelectedField,
  handleClearSelectedFields,
  handleClearSelectedFilters,
}: ComponentProps): JSX.Element {
  const dispatch = useDispatch();

  const canDeleteValue = (value: string): boolean => {
    if (allowDelete) return true;
    if (!allowDeleteFields || !('field' in tree)) return false;
    return allowDeleteFields.some(
      (entry) => entry.field === (tree as DataNode).field && entry.value === value
    );
  };

  const handleClick = (): void => {
    if (!tree) return;
    if (selectedFields.some((field) => field.id === tree.id)) {
      handleRemoveSelectedField(tree as DataNode);
      return;
    }
    handleAddSelectedField(tree as DataNode);
  };

  if (!('field' in tree)) return <></>;

  if (!filterKeysMapping[tree.field as EnvelopeFilterKeys]) {
    captureMessage('Undefined field in filter pills', {
      extra: { field: tree.field, data: tree.data },
    });

    return <></>;
  }

  const renderFieldContent = (): JSX.Element => {
    if (tree.field === 'broad_search' && tree.raw) {
      const value = 'value' in tree.data[0] ? tree.data[0].value : tree.data[0].raw || '';

      const canDeleteReviewSet =
        !isReviewSet ||
        !reviewSetParentFilter ||
        (isReviewSet && !reviewSetParentFilter.includes(value));
      const useAlternateColor =
        isReviewSet && !!reviewSetParentFilter && !reviewSetParentFilter.includes(value);

      return (
        <FilterBadge
          key={tree.id}
          title={value || ''}
          node={
            {
              op: 'word',
              value,
              label: value,
              id: v4(),
              parent: tree.id,
            } as ValueNode
          }
          disabled={!envelopesPage}
          deleteAction={
            canDeleteValue(value) && canDeleteReviewSet && tree.field
              ? (): void => {
                  handleRemoveFieldFromTree(tree.field || '', tree.id);
                  removeFilter(tree.field || '', tree.raw?.replace('~broad_search', '') || '');
                }
              : undefined
          }
          handleAddSelectedFilter={handleAddSelectedFilter}
          handleRemoveSelectedFilter={handleRemoveSelectedFilter}
          selectedFilters={selectedFilters}
          useAlternateColor={useAlternateColor}
        />
      );
    }

    if (tree.field === 'subject' && tree.raw) {
      const subjectValue = tree.raw.replace('~subject', '');
      const canDeleteReviewSet =
        !isReviewSet ||
        !reviewSetParentFilter ||
        (isReviewSet && !reviewSetParentFilter.includes(subjectValue));
      const useAlternateColor =
        isReviewSet && !!reviewSetParentFilter && !reviewSetParentFilter.includes(subjectValue);

      return (
        <FilterBadge
          key={tree.id}
          title={subjectValue}
          node={
            {
              op: 'word',
              value: subjectValue,
              label: subjectValue,
              id: v4(),
              parent: tree.id,
            } as ValueNode
          }
          disabled={!envelopesPage}
          deleteAction={
            canDeleteValue(subjectValue) && canDeleteReviewSet && tree.field
              ? (): void => {
                  handleRemoveValueFromTree(subjectValue, tree.field || '');
                  removeFilter(tree.field || '', subjectValue);
                }
              : undefined
          }
          handleAddSelectedFilter={handleAddSelectedFilter}
          handleRemoveSelectedFilter={handleRemoveSelectedFilter}
          selectedFilters={selectedFilters}
          useAlternateColor={useAlternateColor}
        />
      );
    }

    if (tree.field === 'sample_uuids' && tree.raw) {
      const values = lookAllForField(tree, 'sample_uuids');
      const sampleRawValue = tree.raw.replace('~sample_uuids', '');
      const canDeleteReviewSet =
        !isReviewSet ||
        !reviewSetParentFilter ||
        (isReviewSet && !reviewSetParentFilter.includes('sample_uuids'));
      const useAlternateColor =
        isReviewSet && !!reviewSetParentFilter && !reviewSetParentFilter.includes('sample_uuids');

      if (values && values.length > 0 && renderSampleButton) {
        return (
          <div className="flex flex-row gap-1">
            <FilterBadge
              key={tree.id}
              title={`${values.length} ${pluralize('Sample', values.length)}`}
              node={
                {
                  op: 'word',
                  value: 'Sample',
                  label: 'Sample',
                  id: v4(),
                  parent: tree.id,
                } as ValueNode
              }
              disabled={!envelopesPage}
              deleteAction={
                canDeleteValue(sampleRawValue) && canDeleteReviewSet && tree.field
                  ? (): void => {
                      handleRemoveValueFromTree(sampleRawValue, tree.field || '');
                      removeFilter(tree.field || '', sampleRawValue);
                    }
                  : undefined
              }
              handleAddSelectedFilter={handleAddSelectedFilter}
              handleRemoveSelectedFilter={handleRemoveSelectedFilter}
              selectedFilters={selectedFilters}
              useAlternateColor={useAlternateColor}
            />

            <button
              type="button"
              className="button button--secondary h-5 px-2 py-0"
              onClick={(e): void => {
                e.stopPropagation();
                if (envelopesPage) {
                  dispatch(openExploreSubsamplesModal());
                } else {
                  dispatch(openSubsamplesModal());
                }
              }}
            >
              <span className="text-small">View Samples</span>
            </button>
          </div>
        );
      }
    }

    if (tree.field === 'uuid' && tree.raw) {
      const values = lookAllForField(tree, 'uuid');
      const uuidRawValue = tree.raw.replace('~uuid', '');

      if (values && values.length > 0 && renderSampleButton) {
        // doesn't work like this - too much effort - att: Arkai
        // const useAlternateColor = isReviewSet && !reviewSetParentFilter.includes('uuid');

        return (
          <div className="flex flex-row gap-1">
            <FilterBadge
              key={tree.id}
              title={`${values.length} ${pluralize('Message', values.length)}`}
              node={
                {
                  op: 'word',
                  value: 'Messages',
                  label: 'Messages',
                  id: v4(),
                  parent: tree.id,
                } as ValueNode
              }
              disabled={!envelopesPage}
              deleteAction={
                canDeleteValue(uuidRawValue) && tree.field
                  ? (): void => {
                      handleRemoveValueFromTree(uuidRawValue, tree.field || '');
                      removeFilter(tree.field || '', uuidRawValue);
                    }
                  : undefined
              }
              handleAddSelectedFilter={handleAddSelectedFilter}
              handleRemoveSelectedFilter={handleRemoveSelectedFilter}
              selectedFilters={selectedFilters}
            />

            <button
              type="button"
              className="button button--secondary h-5 px-2 py-0"
              onClick={(e): void => {
                e.stopPropagation();
                if (envelopesPage) {
                  dispatch(openExploreMessagesModal());
                } else {
                  dispatch(openMessagesModal());
                }
              }}
            >
              <span className="text-small">View Messages</span>
            </button>
          </div>
        );
      }
    }

    if (tree.field === 'prompt' && tree.raw) {
      const promptValue = `Prompt: ${tree.params?.name}`;
      const facetValue = `Facet: ${tree.params?.facet}`;
      const scoreValue = `Score: ${tree.params?.score.replace('<>', '-')}`;

      const useAlternateColor =
        isReviewSet && !!reviewSetParentFilter && !reviewSetParentFilter.includes(promptValue);

      return (
        <>
          <FilterBadge
            key={tree.id}
            title={promptValue || ''}
            node={
              {
                op: 'word',
                value: promptValue,
                label: promptValue,
                id: v4(),
                parent: tree.id,
              } as ValueNode
            }
            disabled
            selectedFilters={selectedFilters}
            useAlternateColor={useAlternateColor}
          />
          <FilterBadge
            key={tree.id}
            title={facetValue || ''}
            node={
              {
                op: 'word',
                value: facetValue,
                label: facetValue,
                id: v4(),
                parent: tree.id,
              } as ValueNode
            }
            disabled
            selectedFilters={selectedFilters}
            useAlternateColor={useAlternateColor}
          />
          <FilterBadge
            key={tree.id}
            title={scoreValue || ''}
            node={
              {
                op: 'word',
                value: scoreValue,
                label: scoreValue,
                id: v4(),
                parent: tree.id,
              } as ValueNode
            }
            disabled
            selectedFilters={selectedFilters}
            useAlternateColor={useAlternateColor}
          />
        </>
      );
    }

    return (
      <>
        {tree.data.map((f) => (
          <FilterPillsTree
            tree={f}
            key={f.id}
            removeAllFilter={removeAllFilter}
            showAllFilters={showAllFilters}
            scrolled={scrolled}
            filtersModified={filtersModified}
            removeFilter={removeFilter}
            field={tree.field as string}
            fieldId={tree.id as string}
            allowDelete={allowDelete}
            allowDeleteFields={allowDeleteFields}
            envelopesPage={envelopesPage}
            selectedFilters={selectedFilters}
            selectedFields={selectedFields}
            canChangeConditional={andFilters.includes(`${tree.field as string}_and`)}
            handleRemoveValueFromTree={handleRemoveValueFromTree}
            handleRemoveFieldFromTree={handleRemoveFieldFromTree}
            handleChangeConditionalType={handleChangeConditionalType}
            handleAddSelectedFilter={handleAddSelectedFilter}
            handleRemoveSelectedFilter={handleRemoveSelectedFilter}
            handleAddSelectedField={handleAddSelectedField}
            handleRemoveSelectedField={handleRemoveSelectedField}
            handleClearSelectedFields={handleClearSelectedFields}
            handleClearSelectedFilters={handleClearSelectedFilters}
            isReviewSet={isReviewSet}
            reviewSetParentFilter={reviewSetParentFilter}
          />
        ))}
      </>
    );
  };

  const values = lookAllForField(tree, tree.field || '');

  const parentHasAValue = values.some((v) => reviewSetParentFilter.includes(v));

  const canDeleteReviewSet =
    !isReviewSet || !reviewSetParentFilter || (isReviewSet && !parentHasAValue);

  return (
    <div
      aria-hidden
      role="button"
      className={`relative justify-end text-body grid gap-x-2 rounded px-1 py-0.5 row_field--filter focus:outline-none ${
        selectedFields.find((field) => field.id === tree.id)
          ? 'bg-litlingo-gray-2'
          : 'bg-litlingo-gray-1'
      }`}
      style={{ gridTemplateColumns: 'auto 1fr' }}
      key={tree.id}
      onClick={handleClick}
    >
      {`${filterKeysMapping[tree.field as EnvelopeFilterKeys]} =`}
      <div
        className={`flex flex-row flex-grow-0  gap-0.5 ${
          !scrolled ? 'flex-wrap' : 'flex-no-wrap'
        } } `}
        style={{ flexWrap: showAllFilters || filtersModified ? 'wrap' : undefined }}
      >
        {renderFieldContent()}
      </div>
      {allowDelete && canDeleteReviewSet && (
        <button
          type="button"
          data-testid={`delete-button-${filterKeysMapping[tree.field as EnvelopeFilterKeys]}`}
          className="absolute -top-1 -left-1.5 focus:outline-none cursor-pointer z-100 row_field--filter__delete opacity-0"
          onClick={(e): void => {
            e.stopPropagation();
            if (allowDelete) {
              if (tree.field === 'date_range') {
                dispatch(
                  setURLParams({
                    envelopes__created_after: '',
                    envelopes__created_before: '',
                  })
                );
              }
              handleRemoveFieldFromTree(tree.field as string, tree.id);
              removeAllFilter();
            }
          }}
        >
          <span>{TAG_REMOVE_ICON}</span>
        </button>
      )}
    </div>
  );
}

function ConditionField({
  tree,
  scrolled,
  showAllFilters,
  filtersModified,
  removeAllFilter,
  removeFilter,
  field,
  fieldId,
  envelopesPage,
  allowDelete,
  allowDeleteFields,
  canChangeConditional,
  renderSampleButton,
  selectedFilters,
  selectedFields,
  isReviewSet = false,
  reviewSetParentFilter = '',
  handleRemoveValueFromTree,
  handleRemoveFieldFromTree,
  handleChangeConditionalType,
  handleAddSelectedFilter,
  handleRemoveSelectedFilter,
  handleAddSelectedField,
  handleRemoveSelectedField,
  handleClearSelectedFields,
  handleClearSelectedFilters,
}: ComponentProps): JSX.Element {
  const handleChangeConditional = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    if (!envelopesPage) return;
    if (!canChangeConditional && field) return;
    handleChangeConditionalType(
      tree as DataNode,
      tree.op === Condition.AND ? Condition.OR : Condition.AND
    );
    handleClearSelectedFields();
    handleClearSelectedFilters();
  };

  return (
    <React.Fragment key={tree.id}>
      {(tree as DataNode).data.map((f, i) => (
        <div
          key={f.id}
          className={`flex flex-row items-center gap-0.5 ${
            envelopesPage ? 'flex-no-wrap' : 'flex-wrap flex-grow-0'
          }`}
        >
          <FilterPillsTree
            tree={f}
            key={f.id}
            removeAllFilter={removeAllFilter}
            showAllFilters={showAllFilters}
            scrolled={scrolled}
            filtersModified={filtersModified}
            removeFilter={removeFilter}
            field={field}
            fieldId={fieldId}
            envelopesPage={envelopesPage}
            allowDelete={allowDelete}
            allowDeleteFields={allowDeleteFields}
            canChangeConditional={canChangeConditional}
            renderSampleButton={renderSampleButton}
            selectedFilters={selectedFilters}
            selectedFields={selectedFields}
            handleRemoveValueFromTree={handleRemoveValueFromTree}
            handleRemoveFieldFromTree={handleRemoveFieldFromTree}
            handleChangeConditionalType={handleChangeConditionalType}
            handleAddSelectedFilter={handleAddSelectedFilter}
            handleRemoveSelectedFilter={handleRemoveSelectedFilter}
            handleAddSelectedField={handleAddSelectedField}
            handleRemoveSelectedField={handleRemoveSelectedField}
            handleClearSelectedFields={handleClearSelectedFields}
            handleClearSelectedFilters={handleClearSelectedFilters}
            isReviewSet={isReviewSet}
            reviewSetParentFilter={reviewSetParentFilter}
          />
          {i !== (tree as DataNode).data.length - 1 && (
            <AndOrBadge
              or={tree.op === Condition.OR}
              onClick={(e): void => handleChangeConditional(e)}
              disabled={!canChangeConditional && !!field}
            />
          )}
        </div>
      ))}
    </React.Fragment>
  );
}

export default FilterPillsTree;
